import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTelecallerCallbacklist } from "../../store/telecaller/cases";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import PanelCard from "../../Pages/DashboardPage/components/PanelCard";
import { ReactComponent as Calender } from "../../assets/Icons/calendarIcon.svg";
import { Button, DatePicker, Skeleton, Tooltip } from "antd";
import { PhoneOutlined } from "@ant-design/icons";
import { formatCurrency } from "../../assets/helpers";
import './style.scss'
import { CasesFeedbackModal } from "../AllocationComp/FeedbackModal/CasesFeedback";
import moment from "moment";

const EMPTY = "—";

const Empty = () => <span className="caseEmpty">{EMPTY}</span>;

const hasValue = (text) => text !== null && text !== undefined && text !== "";

/* A day over the time it happened, so a column of them stays scannable. */
const DateTimeCell = ({ text, promise = false }) => {
    if (!hasValue(text)) return <Empty />;
    const when = moment(text);
    if (!when.isValid()) return <span className="caseText">{text}</span>;
    return (
        <span className={`caseDate ${promise ? "is-promise" : ""}`}>
            <span className="caseDate__day">{when.format("DD MMM YYYY")}</span>
            <span className="caseDate__time">{when.format("hh:mm A")}</span>
        </span>
    );
};

const CallBackList = () => {
    const [date, setDate] = useState(null);
    const dispatch = useDispatch();
    const { callback_list, callback_list_loading } = useSelector((state) => state.telecallerCases);
    const { format } = useSelector(state => state.priceFormat);
    const loading = callback_list_loading;
    const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
    const [selectedCase, setSelectedCase] = useState(null);
    const [refresh, setRefresh] = useState(false);
    const [params, setParams] = useState({
        page: 1,
        page_size: 10,
    });
    useEffect(() => {
        dispatch(fetchTelecallerCallbacklist({ ...params }));
    }, [dispatch, params, refresh]);

    const rows = useMemo(
        () => callback_list?.results?.cases_with_feedback || [],
        [callback_list]
    );

    const skeletonData = useMemo(
        () => Array(10).fill(0).map((_, i) => ({ id: `skeleton-${i}` })),
        []
    );

    const columns = [
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> :
                    hasValue(text) ? <span className="caseCustomer">{text}</span> : <Empty />,
        },
        {
            title: 'Loan No.',
            dataIndex: 'loan_no',
            key: 'loan_no',
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> :
                    hasValue(text) ? <span className="caseMono">{text}</span> : <Empty />,
        },
        {
            title: 'Phone Number',
            dataIndex: 'phone_number',
            key: 'phone_number',
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> :
                    hasValue(text) ? <span className="caseMono">{text}</span> : <Empty />,
        },
        {
            title: 'POS',
            dataIndex: 'odpos',
            key: 'odpos',
            align: 'right',
            /* was Number(text).toFixed(2), which printed the string "NaN" on
               any row without a value - and "NaN" is truthy, so the || "-"
               fallback beside it never ran. */
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> :
                    hasValue(text) && Number.isFinite(Number(text))
                        ? <span className="caseAmount">{formatCurrency(text, format)}</span>
                        : <Empty />,
        },

        {
            title: 'Amount Overdue',
            dataIndex: 'amount_overdue',
            key: 'amount_overdue',
            align: 'right',
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> :
                    hasValue(text) ? <span className="caseAmount">{formatCurrency(text, format)}</span> : <Empty />,
        },
        {
            title: 'Bank',
            dataIndex: 'bank_name',
            key: 'bank_name',
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> :
                    hasValue(text) ? <span className="caseText">{text}</span> : <Empty />,
        },

        {
            title: 'Feedback Date',
            dataIndex: 'date',
            key: 'date',
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> : <DateTimeCell text={text} />,
        },
        {
            title: 'Disposition',
            dataIndex: 'call_status',
            key: 'call_status',
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> :
                    hasValue(text) ? (
                        <Tooltip title={text}>
                            <span className="caseDisposition">{text}</span>
                        </Tooltip>
                    ) : <Empty />,
        },
        {
            title: 'Callback Date & Time',
            dataIndex: 'callback_date_time',
            key: 'callback_date_time',
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> : <DateTimeCell text={text} promise />,
        },

        {
            title: 'Comment',
            dataIndex: 'comment',
            key: 'comment',
            /* the full comment is on hover; the cell truncates with CSS so it
               is cut to the column rather than to a fixed 20 characters */
            render: (text) =>
                loading ? <Skeleton.Input size="small" active /> :
                    hasValue(text) ? (
                        <Tooltip title={text}>
                            <span className="caseComment">{text}</span>
                        </Tooltip>
                    ) : <Empty />,
        },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            width: 130,
            render: (_, record) =>
                loading ? <Skeleton.Input size="small" active /> : (
                    <Button
                        className="caseFeedbackBtn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setOpenFeedbackModal(true);
                            setSelectedCase(record);
                        }}
                    >
                        Feedback
                    </Button>
                )
        }
    ];

    const handleFeedbackSubmit = () => {
        setDate(null);
        setParams({ page: 1, page_size: 10 });
        setRefresh(prev => !prev);
    };


    return (
        <div className="callback-list-container">
            <PanelCard
                title="Callback List"
                subtitle={
                    loading
                        ? "Loading callbacks…"
                        : `${callback_list?.count ?? rows.length} callback${(callback_list?.count ?? rows.length) === 1 ? "" : "s"} scheduled`
                }
                icon={<PhoneOutlined />}
                accent="brand"
                padded={false}
                actions={
                    <DatePicker
                        placeholder="Filter by date"
                        className="caseDatePicker"
                        suffixIcon={<Calender />}
                        value={date}
                        onChange={(momentObj, dateString) => {
                            setDate(momentObj);
                            setParams((prev) => {
                                const newParams = { ...prev, page: 1 };
                                if (dateString) {
                                    newParams.date = dateString;
                                } else {
                                    delete newParams.date;
                                }
                                return newParams;
                            });
                        }}
                        format="YYYY-MM-DD"
                    />
                }
            >
                <div className="case-report-table">
                    <ReusableTable
                        columns={columns}
                        dataSource={loading ? skeletonData : rows}
                        /* was hardcoded to 0, so the footer read "Showing 0 - 0
                           of 0 items" and paging past page 1 was impossible */
                        totalItems={callback_list?.count || 0}
                        rowKey="id"
                        CurrentPageNo={params?.page}
                        pageSize={params?.page_size}
                        triggerChange={(page, size) => {
                            setParams((prev) => ({ ...prev, page: page, page_size: size }));
                        }}
                    />
                </div>
            </PanelCard>
            <CasesFeedbackModal
                open={openFeedbackModal}
                setOpen={setOpenFeedbackModal}
                data={selectedCase}
                setRefresh={handleFeedbackSubmit}
            />
        </div>
    )
}
export default CallBackList;
